import React, { useEffect, useMemo, useState } from 'react';
import { useUsersStore } from '../../store/useUsersStore';
import AddEditCategoryModal from './AddEditCategoryModal';
import DeleteModal from '../../components/DeleteModal';


const CategoriesTable = () => {
  // stable single-field subscriptions (avoids getSnapshot caching warning)
  const users = useUsersStore((s) => s.users);
  const selectedKey = useUsersStore((s) => s.selectedKey);
  const loadTemplateCategories = useUsersStore((s) => s.loadTemplateCategories);
  const addTemplateCategory = useUsersStore((s) => s.addTemplateCategory);
  const updateTemplateCategory = useUsersStore((s) => s.updateTemplateCategory);
  const deleteTemplateCategory = useUsersStore((s) => s.deleteTemplateCategory);
  const moveCategoryUp = useUsersStore((s) => s.moveCategoryUp);
  const moveCategoryDown = useUsersStore((s) => s.moveCategoryDown);


  // resolve active sub-user id (same pattern as CountriesFilter)
  const subUserId = useMemo(() => {
    if (!selectedKey || !Array.isArray(users)) return null;
    const found = users.find((u) => {
      const id = String(u.sub_user_id || u.document_id || u.id || '');
      if (id === String(selectedKey)) return true;
      if (u.sub_username && String(u.sub_username) === String(selectedKey)) return true;
      return false;
    });
    return found ? String(found.sub_user_id || found.document_id || found.id) : null;
  }, [selectedKey, users]);


  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [showEntries] = useState(30);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);


  // load/seed categories once per sub-user (store action handles seeding)
  useEffect(() => {
    let mounted = true;
    if (!subUserId) {
      setCategories([]);
      return;
    }

     (async () => {
      setLoading(true);
      try {
        if (typeof loadTemplateCategories !== 'function') {
          console.error('useUsersStore: loadTemplateCategories is not a function');
          if (mounted) setCategories([]);
          return;
        }
        const cats = await loadTemplateCategories(subUserId);
        if (mounted) setCategories(Array.isArray(cats) ? [...cats].sort((a, b) => (a.order || 0) - (b.order || 0)) : []);
      } catch (err) {
        console.error('loadTemplateCategories err', err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
  
    return () => { mounted = false; };
  }, [subUserId, loadTemplateCategories]);


  // keep local categories in sync when users store updates
  useEffect(() => {
    if (!subUserId) return;
    const found = users.find((u) => {
      const id = String(u.sub_user_id || u.document_id || u.id || '');
      return id === String(subUserId);
    });
    const cats = found && Array.isArray(found.template_categories) ? [...found.template_categories] : [];
    setCategories(cats.sort((a, b) => (a.order || 0) - (b.order || 0)));
  }, [users, subUserId]);


  const filtered = categories.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()));


  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (c) => { setEditing(c); setModalOpen(true); };


  const handleSubmit = async (payload) => {
    if (!subUserId) return;
    setLoading(true);
    try {
      if (payload.id) {
        await updateTemplateCategory(subUserId, payload.id, { name: payload.name, alwaysInclude: !!payload.alwaysInclude });
      } else {
        await addTemplateCategory(subUserId, { name: payload.name, alwaysInclude: !!payload.alwaysInclude });
      }
      setModalOpen(false);
    } catch (err) {
      console.error('save category error', err);
      alert('Save failed');
    } finally {
      setLoading(false);
    }
  };


  // Open delete modal
  const openDeleteModal = (category) => {
    setCategoryToDelete(category);
    setDeleteModalOpen(true);
  };


  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setCategoryToDelete(null);
  };


  // Confirm delete
  const confirmDelete = async () => {
    if (!subUserId || !categoryToDelete) return;
    setLoading(true);
    try {
      await deleteTemplateCategory(subUserId, categoryToDelete.id);
      closeDeleteModal();
    } catch (err) {
      console.error('delete category error', err);
      alert('Delete failed');
    } finally {
      setLoading(false);
    }
  };


  const handleMoveUp = async (id) => {
    if (!subUserId) return;
    setLoading(true);
    try {
      await moveCategoryUp(subUserId, id);
    } catch (err) {
      console.error('move up error', err);
    } finally {
      setLoading(false);
    }
  };


  const handleMoveDown = async (id) => {
    if (!subUserId) return;
    setLoading(true);
    try {
      await moveCategoryDown(subUserId, id);
    } catch (err) {
      console.error('move down error', err);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="bg-white rounded shadow p-6">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Template Segment</h2>
        <button 
          onClick={openAdd} 
          className="px-5 py-2.5 bg-white border-[1.5px] border-primary-blue text-primary-blue font-medium text-sm rounded-lg hover:bg-primary-blue hover:text-white transition-colors"
        >
          Add Segment
        </button>
      </div>


      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search here"
            className="w-full px-4 py-3 pr-12 border-[1.5px] border-primary-blue rounded-lg text-sm focus:outline-none focus:ring-primary-blue focus:ring-1 transition-colors"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <svg 
            className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
            />
          </svg>
        </div>
      </div>


      <div className="overflow-x-auto rounded-lg border border-primary-black-shade-10">
        <table className="min-w-full">
          <thead className="bg-primary-blue">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium text-white">Sr.No</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-white">Name</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-white">Always Include?</th>
              <th className="px-6 py-3 text-center text-sm font-medium text-white">Action</th>
            </tr>
          </thead>


          <tbody className="bg-white divide-y divide-primary-black-shade-10">
            {filtered.map((c, idx) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-900">
                  {String((c.order ?? idx) + 1).padStart(2, '0')}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{c.name}</td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {c.alwaysInclude || c.always_include ? 'Yes' : 'No'}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-center gap-2">
                    {/* Edit Icon */}
                    <button
                      onClick={() => openEdit(c)}
                      className="p-1 hover:opacity-70 transition-opacity"
                      title="Edit"
                    >
                      <img 
                        src="/Icons/edit-icon.svg" 
                        alt="Edit" 
                        className="w-6 h-6"
                      />
                    </button>


                    {/* Delete Icon - Now opens modal */}
                    <button
                      onClick={() => openDeleteModal(c)}
                      className="p-1 hover:opacity-70 transition-opacity"
                      title="Delete"
                    >
                      <img 
                        src="/Icons/delete-icon.svg" 
                        alt="Delete" 
                        className="w-6 h-6"
                      />
                    </button>


                    {/* Move Up Icon */}
                    <button
                      onClick={() => handleMoveUp(c.id)}
                      disabled={(c.order ?? idx) === 0}
                      className="p-1 hover:opacity-70 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move Up"
                    >
                      <img 
                        src="/Icons/dropup-icon.svg" 
                        alt="Move Up" 
                        className="w-6 h-6"
                      />
                    </button>


                    {/* Move Down Icon */}
                    <button
                      onClick={() => handleMoveDown(c.id)}
                      disabled={(c.order ?? idx) >= (categories.length - 1)}
                      className="p-1 hover:opacity-70 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move Down"
                    >
                      <img 
                        src="/Icons/dropdown-icon.svg" 
                        alt="Move Down" 
                        className="w-6 h-6"
                      />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                  {loading ? 'Loading...' : 'No categories found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {modalOpen && (
        <AddEditCategoryModal
          isOpen={modalOpen}
          category={editing}
          onClose={() => { setModalOpen(false); setEditing(null); }}
          onSubmit={handleSubmit}
        />
      )}


      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        templateName={categoryToDelete ? `"${categoryToDelete.name}"` : 'this category'}
      />
    </div>
  );
};


export default CategoriesTable;
